import { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

import Toast from 'components/atoms/Toast'
import { useInstallDeviceAccessoryMutation } from 'store/services/devices'
import type { DeviceAccessory, DeviceAccessorySlot } from 'store/types'

interface IProps {
  deviceId: number
  deviceName: string
  // the accessory to put on the device; the modal is closed while null
  slot: DeviceAccessorySlot | null
  // what is on it now, when this is a replacement
  current?: Pick<DeviceAccessory, 'serial' | 'uses'> | null
  onClose: () => void
}

// TES-3191: puts a probe, patch or position sensor on a device, or replaces the one there. The new
// one counts uses from zero; the serial is optional, as not every accessory carries one.
export const AccessoryModal = ({ deviceId, deviceName, slot, current, onClose }: IProps) => {
  const [serial, setSerial] = useState('')
  const [installAccessory, { isLoading }] = useInstallDeviceAccessoryMutation()

  useEffect(() => {
    setSerial('')
  }, [slot])

  if (!slot) return null

  const name = slot.label.toLowerCase()
  const action = current ? 'Replace' : 'Assign'

  const save = async () => {
    try {
      await installAccessory({ id: deviceId, type: slot.type, slot: slot.slot, serial: serial.trim() || null }).unwrap()
      Toast({ type: 'success', label: `${slot.label} ${current ? 'replaced' : 'assigned'}` })
      onClose()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || `Failed to save the ${name}` })
    }
  }

  return (
    <Modal centered show onHide={onClose}>
      <Modal.Header>
        <Modal.Title>
          {action} {slot.label}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3" data-testid="accessory-modal-text">
          {current
            ? `Replace the ${name} on device ${deviceName}? The new one counts uses from zero. `
              + `The current one${current.serial ? ` (${current.serial})` : ''} goes into the device history `
              + `after ${current.uses} ${current.uses === 1 ? 'use' : 'uses'}.`
            : `Record the ${name} on device ${deviceName}? It counts uses from zero.`}
        </p>
        <label htmlFor="accessorySerial" className="textGray">
          {current ? `New ${name} serial` : `${slot.label} serial`}{' '}
          <span className="text-muted font-weight-normal">(optional)</span>
        </label>
        <input
          id="accessorySerial"
          className="form-control"
          value={serial}
          maxLength={150}
          autoFocus
          onChange={(event) => setSerial(event.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save} disabled={isLoading}>
          {action}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
